import { differenceInDays } from 'date-fns';
import { tv } from 'tailwind-variants';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import type { ConferencesEvent, EventsListEvent } from '@/app_lib/types/Conferences';
import { cn, formatStartToEnd } from '@/app_lib/utils/utils';

function getEventLocation(event: EventsListEvent | ConferencesEvent) {
  if (event.isVirtual) {
    return 'Virtual';
  }
  if (!event.location?.country) {
    return '';
  }
  if (!event.location?.city) {
    return event.location.country;
  }
  if (event.location?.state?.code) {
    return `${event.location.city}, ${event.location.state.code}`;
  }
  return `${event.location.city}, ${event.location.country}`;
}

const chipStyles = tv({
  base: 'typography-xs rounded-full border px-2 font-medium uppercase',
  variants: {
    timing: {
      current: 'success-surface success-border success-text! animate-bounce',
      far: 'stroke-secondary text-secondary! bg-pure',
      soon: 'bg-red-50 text-red! stroke-red',
    },
  },
});

export const LearnMoreButton = ({
  externalLink,
  variant,
  size = 'lg',
  className,
  type,
}: {
  externalLink: string;
  size?: 'sm' | 'md' | 'lg';
  variant: 'secondaryOutline' | 'specialBlue' | 'whiteOutline';
  type: 'featured' | 'tile';
  className?: string;
}) => (
  <ButtonV2
    as='a'
    className={cn('max-lg:w-full', className)}
    data-cy='learn-more-button'
    href={externalLink}
    noCapitalize
    optimizelyTrackingData={{ name: `clicked_${type}_event_cta_button` }}
    rel='noopener'
    size={size}
    target='_blank'
    variant={variant}
  >
    Learn more
  </ButtonV2>
);

export const RegisterNowButton = ({
  registerLink,
  variant = 'secondary',
  size = 'lg',
  className,
  type,
}: {
  registerLink: string;
  variant?: 'secondary' | 'secondarySpecialBlue' | 'specialBlue';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  type: 'featured' | 'tile';
}) => (
  <ButtonV2
    as='a'
    className={cn('max-lg:w-full', className)}
    data-cy='register-now-button'
    href={registerLink}
    noCapitalize
    optimizelyTrackingData={{ name: `clicked_${type}_event_cta_button` }}
    rel='noopener'
    size={size}
    target='_blank'
    variant={variant}
  >
    Register now
  </ButtonV2>
);

export const EventDateLocation = ({
  event,
  type = 'primary',
}: {
  event: EventsListEvent | ConferencesEvent;
  type?: 'primary' | 'secondary';
  past?: boolean;
}) => {
  const formattedDate = formatStartToEnd(
    event.startDate,
    event.endDate,
    event.startTimezone,
    event.endTimezone,
    event.dateFormat,
  );

  const daysAway = differenceInDays(new Date(event.startDate), new Date());
  const hasEnded = differenceInDays(new Date(event.endDate), new Date()) < 0;
  const location = getEventLocation(event);

  const chipTiming = daysAway > 0 ? (daysAway < 100 ? 'soon' : 'far') : 'current';
  const timingText = chipTiming === 'current' ? 'Happening now' : `Starts in ${daysAway} days`;

  return (
    <div className={cn('flex flex-col', type === 'primary' && 'typography-level-6')}>
      <div className='flex justify-between items-center'>
        <p
          className='text-secondary'
          data-cy='event-date'
        >
          {event.noDates ? 'Coming soon' : formattedDate}
        </p>
        {!(hasEnded || event.noDates) && (
          <span
            className={chipStyles({ timing: chipTiming })}
            data-cy='event-timing'
          >
            {timingText}
          </span>
        )}
      </div>
      {location && (
        <p
          className='text-secondary'
          data-cy='event-location'
        >
          {location}
        </p>
      )}
      {!location && (
        <p
          aria-hidden='true'
          className='text-secondary invisible'
          data-cy='event-location'
        >
          No location
        </p>
      )}
    </div>
  );
};

// TODO: Topic truncation - stop at 3, should it move to a new line? should we add a +x?
export const EventTopics = ({ event }: { event: EventsListEvent | ConferencesEvent }) => (
  <ul
    className='min-w-0 w-full overflow-hidden'
    data-cy='event-topics'
  >
    {event.eventTopics.length === 0 && (
      <li
        aria-hidden='true'
        className='flex items-center gap-xs text-secondary typography-sm invisible select-none'
        data-cy='event-topic'
      >
        <span>No topics</span>
      </li>
    )}
    {event.eventTopics.length > 0 && (
      <li
        className='text-secondary typography-sm select-none min-w-0 w-full overflow-hidden'
        data-cy='event-topic'
        key={`${event?.title ?? 'Card event'}-topics`}
      >
        <span className='select-text block truncate whitespace-nowrap'>
          {event.eventTopics.slice(0, 3).join(' • ')}
        </span>
      </li>
    )}
  </ul>
);

export const EventTitle = ({ event, className }: { event: EventsListEvent | ConferencesEvent; className?: string }) => (
  <h3
    className={cn('typography-level-1 font-semibold', className)}
    data-cy='event-title'
  >
    {event.title}
  </h3>
);
